import { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AjaxContext, AppContext, ModalContext } from 'commons-ui';
import { actionPerformedSuccessMessage, allertType } from 'pages/dashboard/lottery/lotteryUploadCsv/utils/constants';
import { AxiosResponse } from 'axios';
import {
	ADD_NEW_PAYMENT_GATEWAY_URI,
	GET_ALL_PAYMENT_GATEWAY_LIST_URI,
	GET_CONFIG_URI,
	IAddNewPaymentGatewayInDynamicSwitcherPayload,
	IFormDefaultData,
	IGetDynamicSwitcherPayoutConfigResponse,
	IPaymentGatewayList,
	ITrafficDistributionMap,
	UPDATE_DYNAMIC_SWITCHER_PAYOUT_CONFIG_URI
} from '../../utils/constant';

const useEdit = () => {
	const { postData, getData } = useContext(AjaxContext);
	const {
		app: { appName },
		showAlert
	} = useContext(AppContext);
	const [showLoader, setShowLoader] = useState(false);
	const history = useHistory();
	const [formDefaultData, setFormDefaultData] = useState<IFormDefaultData>({
		upi: {},
		imps: {}
	});
	const [formColumns, setFormColumns] = useState<{ upi: string[]; imps: string[] }>({ upi: [], imps: [] });
	const [paymentGatewayList, setPaymentGatewayList] = useState<IPaymentGatewayList[]>([]);
	const [uniqueUpiHandlerArray, setUniqueUpiHandlerArray] = useState<string[]>([]);
	const [uniqueBankNamesArray, setUniqueBankNamesArray] = useState<string[]>([]);
	const [uniqueUpiHandlerArrayInputText, setUniqueUpiHandlerArrayInputText] = useState<string>('');
	const [uniqueBankNamesArrayInputText, setUniqueBankNamesArrayInputText] = useState<string>('');
	const { setModalConfig } = useContext(ModalContext);

	const getAllPaymentGatewayList = async () => {
		const response = await getData({
			endpoint: GET_ALL_PAYMENT_GATEWAY_LIST_URI,
			queryParams: {
				appName
			}
		});
		return response?.data?.data;
	};

	const fetchConfigData = async (): Promise<void> => {
		try {
			setShowLoader(true);
			const result: AxiosResponse<{ success?: boolean; data?: any }> = await postData({
				endpoint: GET_CONFIG_URI,
				body: {},
				queryParams: {
					appName
				}
			});
			if (!result?.data?.success) throw new Error('Error in fetching the config data!');
			const data: IGetDynamicSwitcherPayoutConfigResponse = result?.data?.data;
			const upiKeys: string[] = Object.keys(data?.upiStaticTrafficDistributionMap || {});
			const impsKeys: string[] = Object.keys(data?.impsStaticTrafficDistributionMap || {});
			const upiData: ITrafficDistributionMap = {};
			const impsData: ITrafficDistributionMap = {};
			upiKeys.forEach((key) => {
				upiData[`upi-${key}`] = data?.upiStaticTrafficDistributionMap[key];
			});
			impsKeys.forEach((key) => {
				impsData[`imps-${key}`] = data?.impsStaticTrafficDistributionMap[key];
			});

			const paymentGatewayListData: IPaymentGatewayList[] = await getAllPaymentGatewayList();

			const formatedPaymentGatewayList: IPaymentGatewayList[] = paymentGatewayListData.filter((item: IPaymentGatewayList) => {
				if (!upiKeys.includes(item.id) || !impsKeys.includes(item.id)) {
					return true;
				}
				return false;
			});

			setPaymentGatewayList([...formatedPaymentGatewayList]);
			setFormColumns({
				upi: [...upiKeys],
				imps: [...impsKeys]
			});
			setFormDefaultData({
				upi: { ...upiData },
				imps: { ...impsData }
			});
			setUniqueUpiHandlerArray([...data?.uniqueUpiHandlerArray]);
			setUniqueBankNamesArray([...data?.uniqueBankNamesArray]);
			setUniqueUpiHandlerArrayInputText(data?.uniqueUpiHandlerArray.join(','));
			setUniqueBankNamesArrayInputText(data?.uniqueBankNamesArray.join(','));
			return;
		} catch (error) {
			showAlert('Error in fetching the config data', { type: 'error' });
			throw error;
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		fetchConfigData();
	}, []);

	const handleFormSubmit = async (submittedData: IFormDefaultData) => {
		try {
			const body: IFormDefaultData = { ...submittedData };
			const upiStaticTrafficDistributionMap: ITrafficDistributionMap = {};
			const impsStaticTrafficDistributionMap: ITrafficDistributionMap = {};
			const formatedUniqueUpiHandlerArray: string[] = [...uniqueUpiHandlerArray].filter((item: any) => item !== '');
			const formatedUniqueBankNamesArray: string[] = [...uniqueBankNamesArray].filter((item: any) => item !== '');
			Object.keys(body?.upi).forEach((item: any) => {
				upiStaticTrafficDistributionMap[item.slice(4, item.length)] = Number(body?.upi?.[item]);
			});
			Object.keys(body?.imps).forEach((item: any) => {
				impsStaticTrafficDistributionMap[item.slice(5, item.length)] = Number(body?.imps?.[item]);
			});
			const payload: IGetDynamicSwitcherPayoutConfigResponse = {
				upiStaticTrafficDistributionMap,
				impsStaticTrafficDistributionMap,
				uniqueUpiHandlerArray: formatedUniqueUpiHandlerArray,
				uniqueBankNamesArray: formatedUniqueBankNamesArray
			};

			setShowLoader(true);
			const response: AxiosResponse<{ success?: boolean }> = await postData({
				endpoint: UPDATE_DYNAMIC_SWITCHER_PAYOUT_CONFIG_URI,
				queryParams: {
					appName
				},
				body: payload
			});
			const err: boolean = response?.data && response?.data?.success;
			if (!err) {
				showAlert(`${response?.data?.error}`, {
					type: allertType
				});
			} else {
				showAlert(actionPerformedSuccessMessage);
				history.goBack();
			}
		} catch (error: any) {
			showAlert(`${error?.response?.data?.error}`, { type: allertType });
		} finally {
			setShowLoader(false);
		}
	};

	const handleFormCancel = () => {
		history.goBack();
	};

	const handleModalClose = () => {
		setModalConfig({ isOpen: false });
	};

	const handleAddNewPg = async (submittedData: IAddNewPaymentGatewayInDynamicSwitcherPayload) => {
		const result: AxiosResponse<{ success?: boolean }> = await postData({
			endpoint: ADD_NEW_PAYMENT_GATEWAY_URI,
			body: { ...submittedData },
			queryParams: {
				appName
			}
		});
		if (result?.data?.success) window.location.reload();
		setModalConfig({ isOpen: false });
	};
	const handleInputChange = (e: any) => {
		const { name, value } = e.target;
		const formatedValue = value.split(',').map((item: any) => item.trim());
		if (name === 'uniqueUpiHandleArray') {
			setUniqueUpiHandlerArray([...formatedValue]);
			setUniqueUpiHandlerArrayInputText(formatedValue.join(','));
		} else {
			setUniqueBankNamesArray([...formatedValue]);
			setUniqueBankNamesArrayInputText(formatedValue.join(','));
		}
	};
	return {
		showLoader,
		handleFormSubmit,
		handleFormCancel,
		setShowLoader,
		formDefaultData,
		formColumns,
		handleAddNewPg,
		handleModalClose,
		paymentGatewayList,
		handleInputChange,
		uniqueUpiHandlerArray,
		uniqueBankNamesArray,
		uniqueBankNamesArrayInputText,
		uniqueUpiHandlerArrayInputText
	};
};

export default useEdit;
